import {
    AnyAction,
    GenericUpload,
    GenericJob,
    Identifiable,
    isUsable,
    LoadingErrorMaybe,
    UploadFlowProps,
    GenericImage,
    JobTypesWithProgressSupport,
    FileExtension,
    UploadStateSummary,
    ID,
    UploadFlowWarnings,
    ImagePreviewType,
    SampleDataFlightKey,
    AddOns,
} from "@nova/constants"
import { RootState } from "integrations/redux"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { createContext, FC, ReactNode, useMemo } from "react"
import { createMultifileUploadSlice } from "@nova/slice-multifile-upload"
import { Action, Selector } from "integrations/redux/store"
import { pick } from "nodash"
import { AsyncThunk } from "@reduxjs/toolkit"
import { ButtonNavigationBack } from "@nova/component-btn"
import {
    clearSelection,
    selectActiveFeatureId,
    selectActiveLayerId,
    setActiveFeatureId,
    setActiveLayerId,
} from "../slice.new"
import { selectEntityById } from "../slice"
import Image from "next/image"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { useUploadFlowContext } from "./useUploadFlowContext"

export type CreateUploadFlowProps = {
    selectSet: (flightId: string) => (s: RootState) => LoadingErrorMaybe<Identifiable>
    selectUploadsForSet: (
        flightId: string
    ) => (s: RootState) => LoadingErrorMaybe<AnyAction>[] | undefined
    selectUploadIdsForSet: (flightId: string) => (s: RootState) => string[]
    selectCancelledUploadIdsForSet: (flightId: string) => (s: RootState) => string[]
    selectJob: (flightId: string) => (state: AnyAction) => AnyAction
    selectSelectedUpload: Selector<Record<string, boolean>>
    setSelectedUpload: Action<Record<string, boolean>>
    setSelectedPlugin: AsyncThunk<void, string | undefined, { state: RootState }>
    // Deletes a staged upload, but not one that has actually been uploaded.
    deleteUpload: ({ uploadId, setId }: { uploadId: string; setId: string }) => AnyAction
    // Deletes an uploaded upload, one that exists in the Protobuf file and is no longer just staged/staging.
    deleteUploadedUpload?: ({
        id,
        context,
    }: {
        id: string
        context: { flightId: string }
    }) => AnyAction
    updateFlight: ({
        id,
        updates,
        context,
    }: {
        id: string
        updates: Record<string, unknown>
        context?: { sampleDataFlightKey?: SampleDataFlightKey }
    }) => AnyAction
    deleteFlight: ({ id }: { id: string }) => AnyAction
    selectFlightById: (id: string) => (state: RootState) => AnyAction
    stageFiles: (
        organizationID: string,
        fireID: string,
        flightID: string,
        files: File[],
        onFinish?: () => void
    ) => ReturnType<ReturnType<typeof createMultifileUploadSlice>["actions"]["stageFiles"]>
    uploadFlight: (
        id: string
    ) => ReturnType<ReturnType<typeof createMultifileUploadSlice>["actions"]["signal"]>
    useProcessing: (setProgress?: (progresses: number[]) => void) => {
        processCallback: (context?: unknown, opts?: unknown) => AnyAction | void | Promise<void>
        uploadCallback?: (flightId: string) => AnyAction
        dependencies: unknown[]
    }
    supportedFileUploadExtensions: FileExtension[]
    fileFilter?: (files: File[]) => Promise<File[]>
    minUploads: number
    maxUploads?: number
    skipProcessing?: boolean
    jobProgressType?: JobTypesWithProgressSupport // If undefined, no progress bar will be shown
    addOnType?: AddOns
    getS3Key?: ({
        orgId,
        projectId,
        flightId,
        fileId,
    }: {
        orgId: string
        projectId: string
        flightId: string
        fileId: string
    }) => string
    selectUploadsForSetSummary: (flightId: string) => (state: AnyAction) => UploadStateSummary
    selectUploadStateSummary: (flightId: string) => (state: AnyAction) => UploadStateSummary
    selectIsUploading: (flightId: string) => (state: AnyAction) => boolean
    selectUploadSize: (flightId: string) => (state: AnyAction) => number
    selectFinishedUploadSize: (flightId: string) => (state: AnyAction) => number
    uploadFlowWarnings?: Record<ID, Record<UploadFlowWarnings, { ids: ID[]; color: string }>>
    setOverlapLayer?: ({ id, active }: { id: ID; active: boolean }) => AnyAction
    selectOverlapLayerIsEnabled?: (flightId: string) => (state: AnyAction) => boolean
    imagePreviewType?: ImagePreviewType
    dropboxTitle?: string
    secondaryPreviewAction?: {
        title: string
        onClick: (id: string) => void
    }
    pluginImageUrl?: string
    pluginId: NovaPluginIds
    sampleDataFlightKey?: SampleDataFlightKey
}

export type CreateUploadFlowAndGetProviderProps = Omit<CreateUploadFlowProps, "RenderComponent">

const uploadFlowContextProps = [
    "selectSelectedUpload",
    "setSelectedUpload",
    "setSelectedPlugin",
    "deleteUpload",
    "deleteUploadedUpload",
    "uploadFlight",
    "stageFiles",
    "useProcessing",
    "supportedFileUploadExtensions",
    "fileFilter",
    "minUploads",
    "maxUploads",
    "selectUploadIdsForSet",
    "selectCancelledUploadIdsForSet",
    "selectUploadsForSet",
    "selectJob",
    "skipProcessing",
    "deleteFlight",
    "updateFlight",
    "selectFlightById",
    "jobProgressType",
    "addOnType",
    "getS3Key",
    "selectUploadsForSetSummary",
    "selectUploadStateSummary",
    "selectIsUploading",
    "selectUploadSize",
    "selectFinishedUploadSize",
    "uploadFlowWarnings",
    "setOverlapLayer",
    "selectOverlapLayerIsEnabled",
    "imagePreviewType",
    "dropboxTitle",
    "secondaryPreviewAction",
    "pluginImageUrl",
    "pluginId",
    "sampleDataFlightKey",
] as const

export type UploadFlowContext = UploadFlowProps &
    Pick<CreateUploadFlowProps, (typeof uploadFlowContextProps)[number]> & {
        flightId: string
        uploads: (GenericImage | LoadingErrorMaybe<GenericUpload>)[]
        usableUploads: (GenericImage | GenericUpload)[]
        erroredUploads: {
            id: string
            error: unknown
        }[]
        job?: GenericJob
        isDemo: boolean
    }

export const UploadFlowContext = createContext({} as UploadFlowContext)

// returns a named function expression, which React reads the display name from
// oxlint-disable-next-line react/display-name
export function CreateUploadFlow(createUploadFlowProps: CreateUploadFlowProps) {
    return function UploadFlow({ children, ...props }: UploadFlowProps & { children: ReactNode }) {
        const { selectUploadsForSet, selectJob } = createUploadFlowProps

        const activeLayerId = useAppSelector(selectActiveLayerId)
        const entity = useAppSelector(selectEntityById(activeLayerId || ""))

        const { id: flightId, portfolioId, fireId } = props
        const uploads = useAppSelector(selectUploadsForSet(flightId))
        const usableUploads = useMemo(
            () =>
                (uploads?.map((upload) => isUsable(upload)).filter((e) => e) as (
                    | GenericUpload
                    | GenericImage
                )[]) || [],
            [uploads]
        )
        const erroredUploads = useMemo(
            () => uploads?.filter((u) => u && "error" in u) || [],
            [uploads]
        ) as {
            id: string
            error: unknown
        }[]

        const job = useAppSelector(selectJob(flightId))

        return (
            <UploadFlowContext.Provider
                value={{
                    ...props,
                    ...pick(
                        createUploadFlowProps,
                        uploadFlowContextProps as unknown as (keyof typeof createUploadFlowProps)[]
                    ),
                    flightId,
                    portfolioId,
                    fireId,
                    uploads: uploads || [],
                    usableUploads,
                    erroredUploads,
                    job,
                    isDemo: !!entity?.demoId,
                }}
            >
                {children}
            </UploadFlowContext.Provider>
        )
    }
}

export function getGenericUploadFlowRenderComponent({
    AddFiles,
    PreviewFiles,
    ProcessingFiles,
}: {
    AddFiles: FC
    PreviewFiles: (props: { hasProcessingStep?: boolean }) => React.ReactElement
    ProcessingFiles?: FC
}) {
    function GenericRenderComponent() {
        const dispatch = useAppDispatch()
        const {
            selectUploadsForSet,
            selectJob,
            pluginImageUrl,
            id: flightId,
            selectFlightById,
        } = useUploadFlowContext()

        const activeLayerId = useAppSelector(selectActiveLayerId)
        const entity = useAppSelector(selectEntityById(activeLayerId || ""))
        const activeFeatureId = useAppSelector(selectActiveFeatureId)

        const uploads = useAppSelector(selectUploadsForSet(flightId))
        const job = useAppSelector(selectJob(flightId))
        const flight = useAppSelector(selectFlightById(flightId))
        const hasImportedImages = (flight?.images?.length ?? 0) > 0

        if (!entity) {
            dispatch(setActiveFeatureId({ feature: undefined }))
            dispatch(clearSelection())
            return null
        }

        return (
            <>
                <ButtonNavigationBack
                    title={"Back"}
                    onClick={() => {
                        dispatch(setActiveLayerId({ id: undefined }))
                        dispatch(setActiveFeatureId({ feature: undefined }))
                    }}
                    className="self-start"
                />
                {activeLayerId && activeFeatureId && (
                    <div className="flex gap-2 items-center mt-3 mb-4">
                        <div className="h-6 w-6 shrink-0">
                            {pluginImageUrl && (
                                <Image
                                    src={pluginImageUrl}
                                    alt=""
                                    width={24}
                                    height={24}
                                    className="w-full h-full object-contain"
                                    priority
                                />
                            )}
                        </div>
                        <h4 className="text-gray-0 overflow-hidden whitespace-nowrap text-ellipsis">
                            {`Create ${entity.name}`}
                        </h4>
                    </div>
                )}
                <div className="flex-1 min-h-0 flex flex-col mb-2">
                    {!uploads?.length && !hasImportedImages ? (
                        <AddFiles />
                    ) : !job || !ProcessingFiles ? (
                        <PreviewFiles hasProcessingStep={!!ProcessingFiles} />
                    ) : (
                        <ProcessingFiles />
                    )}
                </div>
            </>
        )
    }
    return GenericRenderComponent
}
